/**
 * K4x.3a — editing a deposit without opening the whole contract.
 *
 * WHY THIS EXISTS
 * ---------------
 * Correcting a deposit meant opening the full tenancy form: move-in, end date,
 * cancellation quad, notice period, minimum duration, revenue. Everything a
 * tenancy is, to change one confirmation flag. This modal is the deposit and
 * nothing else.
 *
 * WHAT IT DOES NOT DO
 * -------------------
 * It does not decide the status. `security_state` is the backend's answer
 * (K4x.1/K4x.2) and is shown here read-only — there is no status field, no
 * dropdown, and nothing is derived from the flags after saving. The card
 * refreshes from the server, which is the only thing that knows.
 *
 * It also does not carry the payout block (method, IBAN, account holder,
 * amount, note). That block has its own four-way validation and belongs to a
 * single event at the end of a tenancy; putting it here would rebuild the long
 * form this modal exists to escape. It stays in the tenancy form for now.
 *
 * THE PAYLOAD IS A FIXED LIST
 * ---------------------------
 * `buildDepositPatchPayload` names its eight fields one by one. No spread of
 * the tenancy, no spread of the form: PATCH is partial, so a stray key is a
 * silent write to a field nobody meant to touch.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ResponsiveModal } from "../../../../components/ui/responsive-modal";
import { patchAdminTenancy } from "../../../../api/adminData";
import {
  TENANT_DEPOSIT_PROVIDER_LABELS,
  TENANT_DEPOSIT_TYPE_LABELS,
} from "../../unit/unitDetailHelpers";
import { parseOptionalTenantDepositFloat } from "../tenantDetailHelpers";
import {
  depositEditWords,
  depositSecurityState,
  depositTypeHasInsurer,
} from "../../deposits/depositsLabels";
import { StatusPill } from "./overviewUi";

/** Same mapping the card uses — deposits tones onto this panel's pill tones. */
const PILL_TONE = {
  positive: "emerald",
  warn: "amber",
  neutral: "slate",
  muted: "slate",
};

const TYPE_OPTIONS = ["cash", "insurance", "bank", "none"];
const PROVIDER_OPTIONS = [
  "swisscaution",
  "smartcaution",
  "firstcaution",
  "gocaution",
  "other",
];

const FIELD =
  "w-full rounded-[8px] border border-border bg-background px-2.5 py-1.5 " +
  "text-[13px] text-foreground focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring disabled:opacity-50";
const LABEL = "mb-1 block text-[12px] font-medium text-muted-foreground";
const SECTION = "rounded-[10px] border border-border p-3";
const SECTION_TITLE = "mb-2 text-[12px] font-semibold text-foreground";

/** A date-only string for an `<input type="date">`, or "". */
function dateInputValue(raw) {
  if (!raw) return "";
  return String(raw).slice(0, 10);
}

function emptyToNull(value) {
  const s = String(value || "").trim();
  return s === "" ? null : s;
}

/**
 * The eight fields this modal may write, built one by one.
 *
 * Exported so a guard can assert the list without rendering: this is the seam
 * where a careless `{...form}` would turn a deposit correction into a silent
 * write across the tenancy.
 *
 * Provider and premium are sent as explicit null for a non-insurance type, so
 * the wire says what the screen shows. The backend clears them anyway
 * (`_clear_tenant_deposit_provider_if_not_insurance_patch` and its premium
 * sibling) — this is the client agreeing with it, not the client enforcing it.
 */
export function buildDepositPatchPayload(form) {
  const type = emptyToNull(form.type);
  const insured = depositTypeHasInsurer(type);
  return {
    tenant_deposit_type: type,
    tenant_deposit_amount: parseOptionalTenantDepositFloat(form.amount),
    tenant_deposit_provider: insured ? emptyToNull(form.provider) : null,
    tenant_deposit_annual_premium: insured
      ? parseOptionalTenantDepositFloat(form.premium)
      : null,
    deposit_received: !!form.received,
    // Sent as typed. An empty date with the flag on is a legitimate state --
    // "confirmed, date unknown" -- and the backend accepts it; requiring one
    // here would invent an invariant the stored data does not meet.
    deposit_received_at: form.received ? emptyToNull(form.receivedAt) : null,
    deposit_returned: !!form.returned,
    deposit_returned_at: form.returned ? emptyToNull(form.returnedAt) : null,
  };
}

function formFromTenancy(tenancy) {
  return {
    type: String(tenancy?.tenant_deposit_type || "").toLowerCase(),
    amount:
      tenancy?.tenant_deposit_amount != null
        ? String(tenancy.tenant_deposit_amount)
        : "",
    provider: String(tenancy?.tenant_deposit_provider || "").toLowerCase(),
    premium:
      tenancy?.tenant_deposit_annual_premium != null
        ? String(tenancy.tenant_deposit_annual_premium)
        : "",
    received: !!tenancy?.deposit_received,
    receivedAt: dateInputValue(tenancy?.deposit_received_at),
    returned: !!tenancy?.deposit_returned,
    returnedAt: dateInputValue(tenancy?.deposit_returned_at),
  };
}

export default function DepositEditModal({
  tenancy,
  open,
  onOpenChange,
  onSaved,
}) {
  const [form, setForm] = useState(() => formFromTenancy(tenancy));
  const [saving, setSaving] = useState(false);

  // Re-seed on open and whenever the tenancy behind the card changes, so the
  // form never shows one tenancy's figures under another's name.
  useEffect(() => {
    if (open) setForm(formFromTenancy(tenancy));
  }, [open, tenancy]);

  const insured = depositTypeHasInsurer(form.type);
  const words = useMemo(() => depositEditWords(form.type), [form.type]);
  const meta = tenancy?.security_state
    ? depositSecurityState(tenancy.security_state, tenancy.tenant_deposit_type)
    : null;

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (saving || !tenancy?.id) return;
      setSaving(true);
      try {
        await patchAdminTenancy(tenancy.id, buildDepositPatchPayload(form));
        // The parent refetches; nothing here guesses what the state became.
        if (typeof onSaved === "function") await onSaved();
        toast.success("Kaution gespeichert.");
        onOpenChange(false);
      } catch (err) {
        // The modal stays open with the entries intact: a rejected save is a
        // reason to correct something, not to retype everything.
        toast.error(err?.message || "Speichern fehlgeschlagen.");
      } finally {
        setSaving(false);
      }
    },
    [saving, tenancy, form, onSaved, onOpenChange]
  );

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
      title="Kaution bearbeiten"
      description="Nur die Kaution dieses Mietverhältnisses. Der Status ergibt sich aus den gespeicherten Angaben."
      contentClassName="max-w-[560px]"
    >
      <form
        onSubmit={handleSubmit}
        data-testid="deposit-edit-form"
        className="flex min-h-0 flex-1 flex-col text-foreground"
      >
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {meta ? (
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground">Status</span>
              {/* Read-only, from the backend. The form writes the facts; the
                  status is what the backend makes of them. */}
              <StatusPill
                tone={PILL_TONE[meta.tone] || "slate"}
                testId="deposit-edit-status"
              >
                {meta.label}
              </StatusPill>
            </div>
          ) : null}

          <section className={SECTION} data-testid="deposit-edit-section-basics">
            <h3 className={SECTION_TITLE}>Kaution</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={LABEL} htmlFor="deposit-edit-type">
                  Kautionsart
                </label>
                <select
                  id="deposit-edit-type"
                  className={FIELD}
                  value={form.type}
                  disabled={saving}
                  onChange={(e) => set("type")(e.target.value)}
                >
                  <option value="">—</option>
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {TENANT_DEPOSIT_TYPE_LABELS[t] || t}
                    </option>
                  ))}
                </select>
              </div>

              {form.type !== "none" ? (
                <div>
                  <label className={LABEL} htmlFor="deposit-edit-amount">
                    Betrag (CHF)
                  </label>
                  <input
                    id="deposit-edit-amount"
                    className={FIELD}
                    inputMode="decimal"
                    value={form.amount}
                    disabled={saving}
                    onChange={(e) => set("amount")(e.target.value)}
                  />
                </div>
              ) : null}

              {/* Provider and premium belong to a policy. Hidden for every
                  other type, and cleared server-side to match. */}
              {insured ? (
                <>
                  <div>
                    <label className={LABEL} htmlFor="deposit-edit-provider">
                      Anbieter
                    </label>
                    <select
                      id="deposit-edit-provider"
                      className={FIELD}
                      value={form.provider}
                      disabled={saving}
                      onChange={(e) => set("provider")(e.target.value)}
                    >
                      <option value="">—</option>
                      {PROVIDER_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          {TENANT_DEPOSIT_PROVIDER_LABELS[p] || p}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="deposit-edit-premium">
                      Jahresprämie (CHF)
                    </label>
                    <input
                      id="deposit-edit-premium"
                      className={FIELD}
                      inputMode="decimal"
                      value={form.premium}
                      disabled={saving}
                      onChange={(e) => set("premium")(e.target.value)}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </section>

          {form.type !== "none" ? (
            <>
              <section
                className={SECTION}
                data-testid="deposit-edit-section-confirmation"
              >
                <h3 className={SECTION_TITLE}>Bestätigung</h3>
                <label className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    data-testid="deposit-edit-received"
                    checked={form.received}
                    disabled={saving}
                    onChange={(e) => set("received")(e.target.checked)}
                  />
                  {words.received}
                </label>
                {/* Optional on purpose: the backend accepts a confirmed deposit
                    with no date, and stored rows look like that. */}
                <div className="mt-2 max-w-[220px]">
                  <label className={LABEL} htmlFor="deposit-edit-received-at">
                    {words.receivedAt}
                  </label>
                  <input
                    id="deposit-edit-received-at"
                    type="date"
                    className={FIELD}
                    value={form.receivedAt}
                    disabled={saving || !form.received}
                    onChange={(e) => set("receivedAt")(e.target.value)}
                  />
                </div>
              </section>

              <section
                className={SECTION}
                data-testid="deposit-edit-section-return"
              >
                <h3 className={SECTION_TITLE}>Rückgabe / Freigabe</h3>
                <label className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    data-testid="deposit-edit-returned"
                    checked={form.returned}
                    disabled={saving}
                    onChange={(e) => set("returned")(e.target.checked)}
                  />
                  {words.returned}
                </label>
                <div className="mt-2 max-w-[220px]">
                  <label className={LABEL} htmlFor="deposit-edit-returned-at">
                    {words.returnedAt}
                  </label>
                  <input
                    id="deposit-edit-returned-at"
                    type="date"
                    className={FIELD}
                    value={form.returnedAt}
                    disabled={saving || !form.returned}
                    onChange={(e) => set("returnedAt")(e.target.value)}
                  />
                </div>
                <p className="m-0 mt-2 text-[11px] leading-snug text-muted-foreground">
                  Auszahlungsdetails werden weiterhin im Mietverhältnis erfasst.
                </p>
              </section>
            </>
          ) : null}
        </div>

        <div className="mt-3 flex shrink-0 justify-end gap-2 border-t border-border pt-3">
          <button
            type="button"
            disabled={saving}
            onClick={() => onOpenChange(false)}
            className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            data-testid="deposit-edit-save"
            disabled={saving}
            className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3 py-1.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
